import { DynamicForm } from "@shared/components/DynamicForm";
import { useToast } from "@shared/hooks/use-toast";
import { FormField, FormService } from "@shared/services/FormServices";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import React, { useEffect, useMemo } from "react";

interface EditFailureModeDialogProps {
  isOpen: boolean;
  failureMode: any | null;
  onClose: () => void;
  onSubmit: (formData: any) => Promise<void>;
  isUpdating?: boolean;
}

export const EditFailureModeDialog: React.FC<EditFailureModeDialogProps> = ({
  isOpen,
  failureMode,
  onClose,
  onSubmit,
  isUpdating = false,
}) => {
  const { toast } = useToast();

  // Form field definitions
  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "name",
        type: "text",
        label: "Failure Mode Name",
        group: "Failure Mode Details",
        placeholder: "Enter failure mode name...",
        required: true,
        groupColorNeeded: false,
        className: "col-span-1 mt-3",
      },
      {
        name: "description",
        type: "textarea",
        label: "Description",
        group: "Failure Mode Details",
        placeholder: "Enter description...",
        rows: 3,
        required: false,
        className: "col-span-1 mt-3",
      },
    ],
    []
  );

  // Default form values
  const defaultFormValues = {
    name: "",
    description: "",
  };

  // Create form instance
  const editForm = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: defaultFormValues,
  });

  // Populate form when failureMode changes and dialog is open
  useEffect(() => {
    if (failureMode && isOpen) {
      const editData = {
        name: failureMode.name || "",
        description: failureMode.description || "",
      };
      editForm.reset(editData);
    }
  }, [failureMode, isOpen, editForm]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!isOpen) {
      editForm.reset(defaultFormValues);
    }
  }, [isOpen, editForm]);

  // Handle form submission
  const handleSubmit = async (formData: any) => {
    try {
      if (!formData.name?.trim()) {
        toast({
          title: "Error",
          description: "Please enter a failure mode name",
          variant: "destructive",
        });
        return;
      }

      await onSubmit(formData);
    } catch (error) {
      console.error("Error in EditFailureModeDialog:", error);
      // Don't show toast here - let parent handle it
    }
  };

  // Handle dialog close
  const handleClose = () => {
    editForm.reset(defaultFormValues);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Edit Failure Mode {failureMode?.name ? `- ${failureMode.name}` : ""}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <DynamicForm
            key={`edit-${failureMode?.id}-${isOpen}`} // Force re-render when editing different items
            fields={formFields}
            form={editForm}
            onSubmit={handleSubmit}
            onReset={handleClose}
            loading={isUpdating}
            gridColumns={1}
            config={{
              showResetButton: true,
              showSubmitButton: true,
              submitButtonText: isUpdating
                ? "Updating..."
                : "Update Failure Mode",
              resetButtonText: "Cancel",
              spacing: "comfortable",
              classes: {
                form: "space-y-6 p-0",
                button: "px-4 py-2 rounded-md font-medium transition-colors",
                buttonContainer:
                  "flex justify-end gap-3 pt-6 border-gray-200 mt-8",
                submitButton: isUpdating
                  ? "bg-blue-400 text-white cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700",
                resetButton:
                  "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300",
                input:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                textarea:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
                select:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
                label: "block text-sm font-medium text-gray-700 mb-1",
                error: "text-sm text-red-600 mt-1",
                helperText: "text-sm text-gray-500 mt-1",
                container: "space-y-1",
              },
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
